import * as React from 'react'
import {exhaustive} from '@freckle/exhaustive'
import {Alert} from '@frontrow/console/src/components/ui/alert'
import {Card} from '@frontrow/console/src/components/ui/card'
import Input from '@frontrow/console/src/entities/components/base/input'
import {H2} from '@frontrow/console/src/components/typography'
import {type IdentityProviderT} from '@frontrow/console/src/entities/helpers/identity-provider'
import {FIELD_GRID} from '@frontrow/console/src/lib/form-styles'

import LabelledInput from '@frontrow/console/src/components/common/labelled-input'
import FormField from '@frontrow/console/src/components/common/form-field'
import {RenaissanceIDPError} from '@frontrow/console/src/components/common/renaissance-idp-error'

type Props = {
  identityProvider: IdentityProviderT | undefined | null
  syncPaused: boolean
  needsStarIntegrationFields: boolean
  cleverSchoolId: string | undefined | null
  classLinkSourceId: string | undefined | null
  shouldShowDeleteCleverAndClasslinkAssociationsNotification: boolean
  showRenaissanceIDPError: boolean
  onChangeClassLinkSourceId: (classLinkSourceId: string) => void
  onChangeCleverSchoolId: (cleverSchoolId: string) => void
  onChangeIdp: (idp?: IdentityProviderT | null) => void
  onTogglePauseSync: (a?: IdentityProviderT | null) => void
}

// Half a row on a wide window, the full row below `lg` — because half of a
// narrow window is not enough for a 36-character UUID, and a pair of fields
// should break to full width together.
const HALF_WIDE = 'mb-0 pr-0 sm:col-span-12 lg:col-span-6'

export default function IDPConfigurationFormFields(props: Props): React.ReactElement {
  const {
    identityProvider,
    classLinkSourceId,
    cleverSchoolId,
    syncPaused,
    showRenaissanceIDPError,
    needsStarIntegrationFields,
    shouldShowDeleteCleverAndClasslinkAssociationsNotification,

    onChangeClassLinkSourceId,
    onChangeCleverSchoolId,
    onTogglePauseSync,
    onChangeIdp
  } = props

  const classLinkFields =
    identityProvider === 'class-link' ? (
      <FormField
        label="ClassLink Sourced Id"
        htmlFor="class-link-source-id"
        wrapperStyle={HALF_WIDE}
      >
        <Input
          id="class-link-source-id"
          className="font-mono"
          value={classLinkSourceId || ''}
          type="text"
          onChange={onChangeClassLinkSourceId}
        />
      </FormField>
    ) : null

  const cleverFields =
    identityProvider === 'clever' ? (
      <FormField label="Clever Id" htmlFor="clever-school-id" wrapperStyle={HALF_WIDE}>
        <Input
          id="clever-school-id"
          className="font-mono"
          value={cleverSchoolId || ''}
          type="text"
          onChange={onChangeCleverSchoolId}
        />
      </FormField>
    ) : null

  const idpEnabledField =
    identityProvider === null || identityProvider === undefined ? null : (
      <div className="mt-4 border-t border-border pt-4">
        <LabelledInput label={`Pause ${humanReadableIdp(identityProvider)} sync`}>
          <input
            type="checkbox"
            checked={syncPaused}
            onChange={() => onTogglePauseSync(identityProvider)}
          />
        </LabelledInput>
      </div>
    )

  const idpFields =
    cleverFields !== null || classLinkFields !== null ? (
      <div className={`${FIELD_GRID} mt-4`}>
        {cleverFields}
        {classLinkFields}
      </div>
    ) : null

  const renaissanceIdpError = showRenaissanceIDPError ? <RenaissanceIDPError /> : null

  const renaissanceIdpMisconfigurationNotification =
    needsStarIntegrationFields === false && renaissanceIdpError !== null ? (
      <div className="mb-4">
        <Alert type="danger">{renaissanceIdpError}</Alert>
      </div>
    ) : null

  const wipeIdpNotification = shouldShowDeleteCleverAndClasslinkAssociationsNotification ? (
    <div className="mb-4">
      <Alert type="warning">
        Updating the IdP setting will automatically wipe all identifiers from the previous provider.{' '}
        <strong>This action cannot be undone.</strong>
      </Alert>
    </div>
  ) : null

  return (
    <Card htmlElement="section" id="identity-provider">
      <div className="mb-4">
        <H2 addClass="my-0">Identity Provider (IDP)</H2>
      </div>

      {renaissanceIdpMisconfigurationNotification}
      {wipeIdpNotification}
      <div className="flex flex-wrap gap-x-6 gap-y-2">
        <LabelledInput label="None/Admin Roster Sync">
          <input
            type="radio"
            value="none"
            checked={identityProvider === null || identityProvider === undefined}
            onChange={() => onChangeIdp(null)}
          />
        </LabelledInput>
        <LabelledInput label="Renaissance">
          <input
            data-test="renaissance-idp"
            type="radio"
            value="renaissance"
            checked={identityProvider === 'renaissance'}
            onChange={() => onChangeIdp('renaissance')}
          />
        </LabelledInput>

        <LabelledInput label="ClassLink">
          <input
            type="radio"
            value="class-link"
            checked={identityProvider === 'class-link'}
            onChange={() => onChangeIdp('class-link')}
          />
        </LabelledInput>

        <LabelledInput label="Clever">
          <input
            type="radio"
            value="clever"
            checked={identityProvider === 'clever'}
            onChange={() => onChangeIdp('clever')}
          />
        </LabelledInput>
      </div>
      {idpFields}
      {idpEnabledField}
    </Card>
  )
}

function humanReadableIdp(idp: IdentityProviderT): string {
  switch (idp) {
    case 'clever':
      return 'Clever'
    case 'class-link':
      return 'ClassLink'
    case 'renaissance':
      return 'Renaissance'
    case 'csv':
      return ''
    default:
      return exhaustive(idp)
  }
}
